import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { useUser } from '@auth_module/hooks/useUser';
import { logout } from '@auth_module/services';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { ThemeToggler } from '@themes/ThemeToggler';

import { Home } from '@icons/Home';
import { NewspaperIcon } from '@icons/NewspaperIcon';
import { HelpIcon } from '@icons/HelpIcon';
import { Logout } from '@icons/Logout';
import { PuzzleIcon } from '@icons/PuzzleIcon';
import { GearIcon } from '@icons/GearIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { useOrganizations } from '@account_module/hooks/useOrganizations';
import { useAccountContext } from '@account_module/context/AccountContext';

const Avatar = tw.div`relative w-9 h-9 rounded-full bg-accent cursor-pointer drop-shadow-small flex items-center justify-center z-[70]`;
const AvatarMenuWrapper = tw.div`absolute top-[40px] right-0 flex flex-col bg-transparent z-20 transition-all duration-200`;
const AvatarMenu = tw.div`flex flex-col w-[220px] bg-white border border-card-border rounded z-20 p-1 shadow-dropdown`;
const MenuButton = tw.div`flex flex-row items-center justify-start w-full py-[1.2rem] pl-[0.4rem] rounded-[3px] h-[25px] text-[11px] text-muted2 font-semibold cursor-pointer hover:bg-selected`;
const Details = tw.div`bg-special flex items-center justify-start text-left -m-1 mb-1 p-2 border-b border-solid border-card-border`;
const InitialsWrapper = tw.div`bg-accent rounded-full min-w-[24px] min-h-[24px] mr-2 flex items-center justify-center`;
const Initials = tw.div`text-light text-sm whitespace-nowrap h-full w-full flex items-center justify-center select-none`;
const UserName = tw.p`text-sm text-muted text-left font-semibold truncate`;
const UserEmail = tw.p`text-xs text-muted2 text-left truncate`;
const ButtonRouterLink = tw(
  Link
)`flex flex-row items-center justify-start w-full py-[1.2rem] pl-[0.4rem] rounded-[3px] h-[25px] text-[11px] text-muted2 font-semibold cursor-pointer hover:bg-selected no-underline`;
export const Separator = tw.div`h-[1px] w-full bg-muted4 my-6`;
const avatarBackground =
  'conic-gradient(from 221deg at 68.57% 50%, #5364D3 16.875deg, #1C96BC 74.99999821186066deg, #2391B4 116.24999642372131deg, #BA53D3 193.12499284744263deg, #BA53D3 256.8750071525574deg, #5364D3 360deg)';

export const UserAvatar = ({ ...rest }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getOrganizationByIndex } = useOrganizations();
  const organization = getOrganizationByIndex(0);
  const { user } = useUser();
  const { setIsWhatsNewVisible } = useAccountContext();
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const avatarMenuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(avatarMenuRef, () => setIsMenuVisible(false));

  const { isEnabled: isMegaphoneIntegrationEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_INTEGRATION
  );

  const handleLogout = async () => {
    await logout();
  };

  const handleGoToDashboard = () => {
    if (organization?.type === 'customer' || organization?.type === 'customer-free') {
      navigate('/account/self-service-api');
      setIsMenuVisible(false);
    } else {
      navigate('/');
    }
  };

  return (
    <Avatar ref={avatarMenuRef} css={{ background: avatarBackground }} {...rest}>
      <Initials onClick={() => setIsMenuVisible(!isMenuVisible)}>
        {user?.first_name[0]}
        {user?.last_name[0]}
      </Initials>
      {isMenuVisible && (
        <AvatarMenuWrapper>
          <AvatarMenu>
            <Details>
              <InitialsWrapper>
                <Initials tw='text-[11px]'>
                  {user?.first_name[0]}
                  {user?.last_name[0]}
                </Initials>
              </InitialsWrapper>
              <div tw='overflow-hidden'>
                <UserName>
                  {user?.first_name} {user?.last_name}
                </UserName>
                <UserEmail>{user?.email}</UserEmail>
              </div>
            </Details>
            <MenuButton onClick={() => handleGoToDashboard()}>
              <Home tw='stroke-primary align-middle mr-1 mt-[-1px] w-6' />
              {t('common.dashboard')}
            </MenuButton>

            {organization?.type !== 'customer' && (
              <>
                <MenuButton onClick={() => setIsWhatsNewVisible(true)}>
                  <NewspaperIcon tw='stroke-primary align-middle mr-1 w-6' />
                  {t('insights.header.whats_new')}
                </MenuButton>
                <MenuButton onClick={() => navigate('/account/settings')}>
                  <GearIcon tw='stroke-primary align-middle mr-1 w-6' />
                  {t('insights.header.settings')}
                </MenuButton>
              </>
            )}

            <MenuButton
              onClick={() => {
                if (window.Beacon) {
                  window.Beacon('toggle');
                }
              }}>
              <HelpIcon tw='stroke-primary align-middle mr-1 w-6' />
              {t('common.help')}
            </MenuButton>

            {isMegaphoneIntegrationEnabled && organization?.type !== 'customer' && (
              <>
                <div tw='px-1'>
                  <Separator tw='my-1' />
                </div>
                <ButtonRouterLink to='/insights/integrations/megaphone'>
                  <PuzzleIcon tw='stroke-primary align-middle mr-1 w-6' />
                  {t('insights.header.integrations')}
                </ButtonRouterLink>
              </>
            )}

            <MenuButton>
              Theme
              <ThemeToggler />
            </MenuButton>

            <div tw='px-1'>
              <Separator tw='my-1' />
            </div>
            <MenuButton onClick={() => handleLogout()}>
              <Logout tw='fill-text-primary align-middle mr-1 w-6' />
              {t('insights.header.logout')}
            </MenuButton>
          </AvatarMenu>
        </AvatarMenuWrapper>
      )}
    </Avatar>
  );
};
